import Select from '../components/Input/select'
import React, { useEffect, useState } from 'react'
import ExpandableContainer from '../components/ui/expandable-container'
import { CucinaResponseJSON } from '../types/output-data'
import {
  getCucinaOptionsForStepResponse,
  getCucinaRecipeResponse
} from '../services/backend-services'
import {
  extractCucinaResponseDataFromJSON,
  extractCucinaStepOptionsFromJSON
} from '../utils/extract-data-from-json'
import SignalsService from '../services/communication/signals.service'
import { SIGNAL_EVENT } from '../types/enums'
import { capitalizeString } from '../utils/formatting'
import PostMessageService, { PostMessageType } from '../services/communication/post-message.service'
import Button from '../components/ui/button'
import ErrorMessage from '../components/ui/error-message'
import Resizer from '../components/ui/resizer'
import Number from '../components/ui/number'

const courses = ['Carne', 'Dolce', 'Pasta', 'Pesce', 'Verdura', 'Zuppa']

/** Pagina Cucina che permette la visualizzazione dei numeri consigliati da giocare sulla base di una ricetta scelta in base alle opzioni selezionate dall'utente
 */
const NumeriVincentiCucina = () => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [showNumbers, setShowNumbers] = useState(false)

  const [stepOptions, setStepOptions] = useState<Array<string[]>>([courses])
  const [userSelectedOptions, setUserSelectedOptions] = useState<Array<string>>([])

  // risposta finale che si otterrà conclusa la ricetta
  const [response, setResponse] = useState<CucinaResponseJSON>({
    ingredients: [],
    numbers: [],
    preparationSteps: '',
    wheel: '',
    recipe: ''
  })
  const [isResponseError, setIsResponseError] = useState<boolean>(false)

  const cucinaTestId = 'cucina'
  const selectStepText = ['Portata Scelta', 'Primo Ingrediente', 'Secondo Ingrediente', 'Ricetta']
  const currentStep = userSelectedOptions?.length
  const numStep = selectStepText.length

  // aggiornamento delle risposte
  const updateAnswer = (answer: string, idxStep: number) => {
    if (idxStep >= 0) {
      setUserSelectedOptions((prevAnswer) => {
        let newAnswer: string[]

        if (idxStep <= prevAnswer.length - 1) {
          // Se la select modificata si trova prima di altre select con risposta,
          // resetta tutte le risposte successive
          newAnswer = [...prevAnswer.slice(0, idxStep)]
        } else {
          // altrimenti mantieni le risposte precedenti
          newAnswer = [...prevAnswer]
        }

        // Aggiungi o aggiorna la risposta per la select corrente
        newAnswer[idxStep] = answer

        return newAnswer.filter((key) => key !== '')
      })
    }
  }

  // aggiorna le options delle select fino allo step corrente
  const updateOptions = (currentStepOptions: string[]) => {
    setStepOptions((prevStepOptions) => {
      const newStepOptions = [...prevStepOptions.slice(0, currentStep + 1)]
      newStepOptions[currentStep] = currentStepOptions
      return newStepOptions
    })
  }

  // resetta le options delle select fino allo step indicato
  const resetOptionsTillStep = (step: number) => {
    setStepOptions((prevStepOptions) => {
      return [...prevStepOptions.slice(0, step + 1)]
    })
  }

  const initState = () => {
    // l'init options per mantenere quelle del primo step e del secondo
    resetOptionsTillStep(0)
    // init risposte utente deve settare la risposta della prima select con la prima option
    setUserSelectedOptions([])
  }

  // reset di tutti gli stati ai valori iniziali
  const resetAllState = () => {
    initState()
    setIsButtonDisabled(true)
    setShowNumbers(false)
    setResponse({
      ingredients: [],
      numbers: [],
      preparationSteps: '',
      wheel: '',
      recipe: ''
    })
  }

  const handleButtonClick = () => {
    setShowNumbers(false)
    initState()
  }

  const handleSelectOnChange = (e: React.ChangeEvent<HTMLSelectElement>, idxStep: number) => {
    setIsResponseError(false)
    updateAnswer(e.target.value, idxStep)
  }

  const handleResponseError = () => {
    setIsResponseError(true)
  }

  useEffect(() => {
    return () => {
      resetAllState()
    }
  }, [])

  useEffect(() => {
    if (isResponseError) {
      updateAnswer('', currentStep - 1)
    }
  }, [isResponseError])

  useEffect(() => {
    // Funzione asincrona per recuperare la ricetta con timeout
    const fetchRecipe = async () => {
      try {
        SignalsService.fire(SIGNAL_EVENT.showLoader, 'flex')
        // Competizione tra il timeout e la chiamata API, per evitare che il loader si veda all'infinito
        const responseJson = await getCucinaRecipeResponse(
          userSelectedOptions[0],
          userSelectedOptions[1],
          userSelectedOptions[2],
          userSelectedOptions[3]
        )
        const response = extractCucinaResponseDataFromJSON(responseJson)
        setResponse(response)
      } catch (e) {
        if (e instanceof Error) {
          console.error('Numeri vincenti cucina:', e.message)
          handleResponseError()
        }
      } finally {
        SignalsService.fire(SIGNAL_EVENT.showLoader, 'hidden')
      }
    }

    // Funzione asincrona per recuperare le opzioni per ogni step con timeout
    const fetchStepOptions = async () => {
      try {
        SignalsService.fire(SIGNAL_EVENT.showLoader, 'flex')
        const responseJson = await getCucinaOptionsForStepResponse(
          currentStep,
          userSelectedOptions[0],
          userSelectedOptions[1],
          userSelectedOptions[2]
        )
        const options = extractCucinaStepOptionsFromJSON(responseJson, currentStep)
        updateOptions(options)
      } catch (e) {
        console.error('Numeri vincenti cucina: ', e)
        handleResponseError()
      } finally {
        SignalsService.fire(SIGNAL_EVENT.showLoader, 'hidden')
      }
    }

    // se tutti gli step hanno una risposta selezionata, recupero e mostro la ricetta disabilitando il bottone
    if (currentStep === numStep) {
      fetchRecipe()
    } else {
      // altrimenti devo fetchare le opzioni dei vari step a cui l'utente non ha ancora risposto
      // (lo step precedente deve avere una risposta non vuota)
      if (currentStep > 0 && userSelectedOptions[currentStep - 1] !== '') fetchStepOptions()
    }
  }, [userSelectedOptions])

  useEffect(() => {
    if (
      response &&
      response.ingredients &&
      response.wheel &&
      response.numbers &&
      response.numbers.length > 0 &&
      response.recipe &&
      response.preparationSteps
    ) {
      setIsButtonDisabled(false)
      setShowNumbers(true)
      // Serve per comunicare con il lotto, per far mostrare/nascondere eventuali contenuti visibili solo dopo aver ottenuto i numeri
      PostMessageService.send({
        type: PostMessageType.PLAY_NUMBERS,
        numbers: response.numbers
      })
    }
  }, [response])

  useEffect(() => {
    if (currentStep !== numStep) {
      PostMessageService.send({
        type: PostMessageType.RESET_NUMBERS
      })
      setIsButtonDisabled(true)
      setShowNumbers(false)
    }
  }, [userSelectedOptions])

  // gestione apparizione numeri tramite animazione. Se i numeri devono sparire, resetta le risposte e reinizializza lo stato
  useEffect(() => {
    const timeoutId: NodeJS.Timeout[] = []
    if (!showNumbers) {
      // attendi che l'animazione finisce per resettare lo stato
      // TODO: c'è un modo migliore?
      const t = setTimeout(() => {
        setIsButtonDisabled(true)
        setResponse({
          ingredients: [],
          numbers: [],
          preparationSteps: '',
          wheel: '',
          recipe: ''
        })
      }, 1000)
      timeoutId.push(t)
    }

    return () => {
      timeoutId.map((t) => {
        clearTimeout(t)
      })
    }
  }, [showNumbers])

  return (
    <Resizer idResizer={cucinaTestId}>
      <div className={'w-full h-full text-base bg-lightOrangeColor'}>
        <form className={'grid grid-cols-2 mobile:grid-cols-1'}>
          <div className={'py-5 col-span-2'}>
            <div className={'px-3.7'}>
              <div className={'px-3.7'}>
                <p>Trova la tua ricetta</p>
              </div>
            </div>
          </div>
          <div id={'scelta'} className={'py-5 col-span-2 grid grid-cols-2 mobile:grid-cols-1'}>
            {selectStepText.map((currTxt, idx) => (
              <div
                className={'py-3.7 col-span-2 grid grid-cols-2 mobile:grid-cols-1'}
                key={currTxt}>
                <div className={'text-greyColor leading-[60px] flex'}>
                  <div
                    className={
                      'w-[55px] h-[55px] leading-[55px] font-[600] flex items-center justify-center'
                    }>
                    {idx + 1}
                  </div>
                  <div className={'uppercase font-medium leading-[55px] text-[1.1em]'}>
                    {currTxt}:
                  </div>
                </div>
                <Select
                  idSelect={'domanda' + idx.toString()}
                  options={stepOptions[idx] ?? []}
                  onChange={(e) => handleSelectOnChange(e, idx)}
                  isDefaultNone
                  value={userSelectedOptions[idx] || ''}
                  isDisabled={!stepOptions[idx]}
                  customClassName={'uppercase'}
                />
              </div>
            ))}
          </div>
          <div className={'py-5 col-span-2'}>
            <div className={'px-3.7 w-full flex items-center justify-center'}>
              <Button
                idButton={cucinaTestId}
                buttonText={'cerca una nuova ricetta'}
                disabled={isButtonDisabled}
                className={'max-h-[54.8px]'}
                handleClick={handleButtonClick}
              />
            </div>
          </div>
          <ExpandableContainer
            id={cucinaTestId}
            expandableCondition={showNumbers}
            className={`transition-vertical-spacing duration-1000 ease-out overflow-hidden px-3.7 col-span-2`}>
            <div id={'ricetta'} className={'py-5'}>
              <div className={'px-3.7'}>
                <div className={'flex justify-center items-center'}>
                  <h2>{response?.recipe}</h2>
                </div>
                <h2 className={'m-0'}>Ingredienti:</h2>
                <p>
                  {response?.ingredients?.map((ingrediente: string, index: number) => (
                    <span key={index} className={'flex flex-col'}>
                      {capitalizeString(ingrediente)}
                    </span>
                  ))}
                </p>
                <h2 className={'m-0'}>Preparazione:</h2>
                <p className={'flex flex-col'}>
                  {response?.preparationSteps?.split('\n').map((prepStep, index) => {
                    return <span key={index}>{prepStep}</span>
                  })}
                </p>
              </div>
              <div className={'px-3.7 grid grid-cols-3 mobile:grid-cols-1'}>
                <div className={'px-3.7'}>
                  <h2 className={'desktop:pt-4 tablet:pt-4'}>Lo chef Consiglia:</h2>
                </div>
                <div
                  id={cucinaTestId + '-numbers-container'}
                  className={'px-3.7 flex justify-around items-center'}>
                  {response?.numbers?.map((numero: string, idx) => (
                    <Number
                      idNumber={`${cucinaTestId}-${idx}`}
                      numberTxt={numero}
                      key={`${cucinaTestId}-${numero}-${idx}`}
                      customClassName={'mx-1.5 mobile:mx-3.7 h-[50px] desktop:w-[50px] flex-grow'}
                    />
                  ))}
                </div>
                <div>
                  <h2>
                    sulla ruota di <br /> {response?.wheel}
                  </h2>
                </div>
              </div>
            </div>
          </ExpandableContainer>
        </form>
      </div>
      <ErrorMessage idError={cucinaTestId} isVisible={isResponseError} />
    </Resizer>
  )
}

export default NumeriVincentiCucina
